import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, CreditCard, Building2, Smartphone, Copy, CheckCircle, AlertTriangle, Clock, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { publicInvoiceAPI } from '../lib/api';
import { toast, Toaster } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_25355c40-a335-460f-bd4b-faf08ba5507b/artifacts/71a1dr43_logo%20without%20background.png";

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Clock },
  sent: { label: 'Pending', color: 'bg-blue-100 text-blue-800', icon: Clock },
  partially_paid: { label: 'Partially Paid', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: AlertTriangle },
};

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [token]);

  const fetchInvoice = async () => {
    try {
      const { data } = await publicInvoiceAPI.getInvoice(token);
      setInvoice(data);
    } catch (err) {
      setError('Invoice not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await publicInvoiceAPI.downloadPDF(token);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number.replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handlePayWithRazorpay = async () => {
    if (!invoice.razorpay_enabled) {
      toast.error('Online payments are not enabled');
      return;
    }

    setProcessingPayment(true);
    try {
      const { data: order } = await publicInvoiceAPI.createPaymentOrder(token);
      
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'VIBE6 DIGITAL LLP',
        description: order.description,
        order_id: order.order_id,
        prefill: {
          name: order.customer_name,
          email: order.customer_email
        },
        theme: {
          color: '#0891b2'
        },
        handler: async function(response) {
          try {
            await publicInvoiceAPI.verifyPayment(token, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setPaymentSuccess(true);
            toast.success('Payment successful! Thank you.');
            fetchInvoice();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error('Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md text-center">
          <CardContent className="pt-6">
            <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Invoice Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md text-center">
          <CardContent className="pt-6">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for your payment. A receipt has been sent to your email.
            </p>
            <Button onClick={() => setPaymentSuccess(false)}>View Invoice</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const config = statusConfig[invoice.status] || statusConfig.sent;
  const StatusIcon = config.icon;
  const bankDetails = invoice.company_bank_details || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Vibe6" className="h-10" />
          <Badge className={`${config.color} flex items-center gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {config.label}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Invoice Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Invoice {invoice.invoice_number}
            </h1>
            <p className="text-muted-foreground">{invoice.project_display_name}</p>
          </div>
          <Button variant="outline" onClick={handleDownloadPDF}>
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>

        {/* Amount Due Card */}
        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <Card className="border-primary bg-primary/5">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-sm text-muted-foreground">Balance Due</p>
                  <p className="text-4xl font-bold text-primary">₹{invoice.balance_due?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Due: {invoice.due_date}</p>
                </div>
                {invoice.razorpay_enabled && (
                  <Button size="lg" onClick={handlePayWithRazorpay} disabled={processingPayment}>
                    <CreditCard className="h-5 w-5 mr-2" />
                    {processingPayment ? 'Processing...' : 'Pay Now'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {invoice.status === 'paid' && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6 text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <h2 className="text-xl font-bold text-green-700">Invoice Paid</h2>
              <p className="text-green-600">Thank you for your payment!</p>
            </CardContent>
          </Card>
        )}

        {/* Invoice Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Bill To</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{invoice.customer_name}</p>
              <p className="text-sm text-muted-foreground">Invoice Date: {invoice.invoice_date}</p>
              <p className="text-sm text-muted-foreground">Due Date: {invoice.due_date}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Amount:</span>
                <span className="font-medium">₹{invoice.total_amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="font-medium text-green-600">₹{invoice.amount_paid?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Balance Due:</span>
                <span className="font-bold text-primary">₹{invoice.balance_due?.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm">
                    <th className="p-3">Description</th>
                    <th className="p-3">Qty</th>
                    <th className="p-3 text-right">Rate</th>
                    <th className="p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoice.line_items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-3">
                        {item.description}
                        {item.period && <span className="text-sm text-muted-foreground block">({item.period})</span>}
                      </td>
                      <td className="p-3">{item.quantity} {item.unit}</td>
                      <td className="p-3 text-right">₹{item.unit_price?.toLocaleString()}</td>
                      <td className="p-3 text-right font-medium">
                        ₹{((item.amount || 0) + (item.tax_amount || 0)).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <>
            <h2 className="text-xl font-bold pt-4">Payment Options</h2>
            
            {/* Bank Transfer */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5" />
                  Bank Transfer (NEFT/RTGS/IMPS)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Name</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{bankDetails.company_name}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.company_name, 'Account name')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bank</p>
                      <p className="font-medium">{bankDetails.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Branch</p>
                      <p className="font-medium">{bankDetails.branch}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Number</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{bankDetails.account_number}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.account_number, 'Account number')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">IFSC Code</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{bankDetails.ifsc}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.ifsc, 'IFSC code')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">SWIFT Code</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{bankDetails.swift_code}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.swift_code, 'SWIFT code')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* UPI Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Smartphone className="h-5 w-5" />
                  UPI Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-1">
                    <div className="mb-4">
                      <p className="text-sm text-muted-foreground">UPI ID</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono text-lg">{bankDetails.upi_id}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.upi_id, 'UPI ID')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Open any UPI app (Google Pay, PhonePe, Paytm, etc.) and pay using the UPI ID above or scan the QR code.
                    </p>
                  </div>
                  {bankDetails.upi_qr_url && (
                    <div className="bg-white p-4 rounded-lg border">
                      <img 
                        src={bankDetails.upi_qr_url} 
                        alt="UPI QR Code" 
                        className="w-48 h-48 object-contain"
                      />
                      <p className="text-center text-sm text-muted-foreground mt-2">Scan to Pay</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Terms */}
        {invoice.payment_terms && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{invoice.payment_terms}</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} VIBE6 DIGITAL LLP. All rights reserved.</p>
          <p className="mt-1">For queries, contact accounts@vibe6.com</p>
        </div>
      </footer>
    </div>
  );
}
